import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { FormsModule } from '@angular/forms';
import { FinalReportService, FinalReportTestDataRequestDto } from '../../services/final-report.service';

export interface FinalReportDialogData {  
  projectId: number;
  projectTypeId: number;
  slurries: any[];
  serviceCompanyName: string;
  userEmail: string;
}

export interface FinalReportTestData {
  thickeningTimes: any[];
  freeFluids: any[];
  fluidLosses: any[];
  rheologies: any[];
  compressiveStrengthUca: any[];
  crushCubes: any[];
  staticGelStrengths: any[];
  foamStabilities: any[];
  foamCrushes: any[];
  spacerCompatibilities: any[];
  spacerQaqcTests: any[];
  spacerSsstTests: any[];
  spacerHphtRheologies: any[];
}

@Component({
  selector: 'app-final-report-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule , MaterialModule],
  templateUrl: './final-report-dialog.component.html',
  styleUrls: ['./final-report-dialog.component.css']
})
export class FinalReportDialogComponent implements OnInit {
  projectId!: number;
  slurries: any[] = [];
  serviceCompanyName = '';

  currentStep = 1;
  selectedSlurryId: number = 0;
  selectedThickeningTimeId: number | null = null;
  selectedFluidLossId: number | null = null;
  selectedFreeFluidId: number | null = null;
  selectedRheologyId: number | null = null;
  selectedUcaId: number | null = null;
  selectedCrushCubeId: number | null = null;
  showUnapprovedTests = false;
  isLoadingTests = false;
  isGenerating = false;

  testData: FinalReportTestData = {
    thickeningTimes: [],
    freeFluids: [],
    fluidLosses: [],
    rheologies: [],
    compressiveStrengthUca: [],
    crushCubes: [],
    staticGelStrengths: [],
    foamStabilities: [],
    foamCrushes: [],
    spacerCompatibilities: [],
    spacerQaqcTests: [],
    spacerSsstTests: [],
    spacerHphtRheologies: []
  };

  // Service Company tests — used as radio-button options in Step 3
  scThickeningTimes: any[] = [];
  scFreeFluids: any[] = [];
  scFluidLosses: any[] = [];
  scRheologies: any[] = [];
  scUcaTests: any[] = [];
  scCrushCubes: any[] = [];
  scGelStrengths: any[] = [];
  scFoamStabilities: any[] = [];
  scFoamCrushes: any[] = [];
  scSpacerCompatibilities: any[] = [];
  scSpacerQaqcTests: any[] = [];
  scSpacerSsstTests: any[] = [];

  // All available tests (including unapproved) — kept for reference
  allTestData: FinalReportTestData = {
    thickeningTimes: [],
    freeFluids: [],
    fluidLosses: [],
    rheologies: [],
    compressiveStrengthUca: [],
    crushCubes: [],
    staticGelStrengths: [],
    foamStabilities: [],
    foamCrushes: [],
    spacerCompatibilities: [],
    spacerQaqcTests: [],
    spacerSsstTests: [],
    spacerHphtRheologies: []
  };
  userEmail: string = '';
  projectTypeId: number = 1;
  

  constructor(
    public dialogRef: MatDialogRef<FinalReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: FinalReportDialogData,
    private finalReportService: FinalReportService
  ) {}

  ngOnInit(): void {
    
    this.projectId = this.data.projectId;
    this.slurries = this.data.slurries;
    this.serviceCompanyName = this.data.serviceCompanyName;
    this.selectedSlurryId = 0;
    this.userEmail = this.data.userEmail;
    this.projectTypeId = this.data.projectTypeId;
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      if (!this.hasSelectedSlurry()) {
        return;
      }
      this.loadTestData();
      return; // wait for data to load before advancing
    }
    if (this.currentStep === 2) {
      this.ensureThickeningMatchSelection();
    }
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  private loadTestData(): void {
    this.isLoadingTests = true;

    const request: FinalReportTestDataRequestDto = {
      projectId: this.projectId,
      projectTypeId: this.projectTypeId,
      slurryId: this.selectedSlurryId,
      includeUnapproved: this.showUnapprovedTests
    };
    
    this.finalReportService.getFinalReportTestData(request)
      .subscribe({
        next: (data) => {
          this.applyTestData(data);
          this.isLoadingTests = false;
          this.currentStep = 2;
        },
        error: (err) => {
          console.error('Error loading test data:', err);
          this.isLoadingTests = false;
          this.currentStep = 2;
        }
      });
  }

  private applyTestData(data: any): void {
    const payload = data || {};
    const thickeningTimes = payload.thickeningTimes ?? payload.ThickeningTimes ?? [];
    const freeFluids = payload.freeFluids ?? payload.FreeFluids ?? [];
    const fluidLosses = payload.fluidLosses ?? payload.FluidLosses ?? [];
    const rheologies = payload.rheologies ?? payload.Rheologies ?? [];
    const compressiveStrengthUca = payload.compressiveStrengthUca ?? payload.CompressiveStrengthUca ?? [];
    const crushCubes = payload.crushCubes ?? payload.CrushCubes ?? [];
    const spacerCompatibilities = payload.spacerCompatibilities ?? payload.SpacerCompatibilities ?? [];
    const spacerQaqcTests = payload.spacerQaqcTests ?? payload.SpacerQaqcTests ?? [];
    const spacerSsstTests = payload.spacerSsstTests ?? payload.SpacerSsstTests ?? [];
    const spacerHphtRheologies = payload.spacerHphtRheologies ?? payload.SpacerHphtRheologies ?? [];
    const staticGelStrengths = payload.staticGelStrengths ?? payload.StaticGelStrengths ?? [];
    const foamStabilities = payload.foamStabilities ?? payload.FoamStabilities ?? [];
    const foamCrushes = payload.foamCrushes ?? payload.FoamCrushes ?? [];

    const addFlags = (t: any) => ({ ...t, isSelected: false, isMatchSelected: false, matchedScId: null });
    this.testData = {
      thickeningTimes: thickeningTimes.map(addFlags),
      freeFluids: freeFluids.map(addFlags),
      fluidLosses: fluidLosses.map(addFlags),
      rheologies: rheologies.map(addFlags),
      compressiveStrengthUca: compressiveStrengthUca.map(addFlags),
      crushCubes: crushCubes.map(addFlags),
      spacerCompatibilities: spacerCompatibilities.map(addFlags),
      spacerQaqcTests: spacerQaqcTests.map(addFlags),
      spacerSsstTests: spacerSsstTests.map(addFlags),
      spacerHphtRheologies: spacerHphtRheologies.map(addFlags),
      staticGelStrengths: staticGelStrengths.map(addFlags),
      foamStabilities: foamStabilities.map(addFlags),
      foamCrushes: foamCrushes.map(addFlags)
    };

    const extract = (key: string, fallback: string) => payload[key] ?? payload[fallback] ?? [];
    this.allTestData = {
      thickeningTimes: extract('thickeningTimes', 'ThickeningTimes'),
      freeFluids: extract('freeFluids', 'FreeFluids'),
      fluidLosses: extract('fluidLosses', 'FluidLosses'),
      rheologies: extract('rheologies', 'Rheologies'),
      compressiveStrengthUca: extract('compressiveStrengthUca', 'CompressiveStrengthUca'),
      crushCubes: extract('crushCubes', 'CrushCubes'),
      spacerCompatibilities: extract('spacerCompatibilities', 'SpacerCompatibilities'),
      spacerQaqcTests: extract('spacerQaqcTests', 'SpacerQaqcTests'),
      spacerSsstTests: extract('spacerSsstTests', 'SpacerSsstTests'),
      spacerHphtRheologies: extract('spacerHphtRheologies', 'SpacerHphtRheologies'),
      staticGelStrengths: extract('staticGelStrengths', 'StaticGelStrengths'),
      foamStabilities: extract('foamStabilities', 'FoamStabilities'),
      foamCrushes: extract('foamCrushes', 'FoamCrushes')
    };
    // Service Company tests — the correct radio-button options
    this.scThickeningTimes      = extract('scThickeningTimes',      'ScThickeningTimes');
    this.scFreeFluids           = extract('scFreeFluids',           'ScFreeFluids');
    this.scFluidLosses          = extract('scFluidLosses',          'ScFluidLosses');
    this.scRheologies           = extract('scRheologies',           'ScRheologies');
    this.scUcaTests             = extract('scUcaTests',             'ScUcaTests');
    this.scCrushCubes           = extract('scCrushCubes',           'ScCrushCubes');
    this.scSpacerCompatibilities = extract('scSpacerCompatibilities', 'ScSpacerCompatibilities');
    this.scSpacerQaqcTests      = extract('scSpacerQaqcTests',      'ScSpacerQaqcTests');
    this.scSpacerSsstTests      = extract('scSpacerSsstTests',      'ScSpacerSsstTests');
    this.scGelStrengths         = extract('scGelStrengths',         'ScGelStrengths');
    this.scFoamStabilities      = extract('scFoamStabilities',      'ScFoamStabilities');
    this.scFoamCrushes          = extract('scFoamCrushes',          'ScFoamCrushes');

    this.selectedThickeningTimeId = null;
    this.selectedFluidLossId = null;
    this.selectedFreeFluidId = null;
    this.selectedRheologyId = null;
    this.selectedUcaId = null;
    this.selectedCrushCubeId = null;
  }

  private ensureThickeningMatchSelection(): void {
    const selected = this.getSelectedThickeningTimes();
    if (selected.length === 0) {
      this.selectedThickeningTimeId = null;
      return;
    }

    const matchSelected = selected.find(t => t.isMatchSelected);
    if (matchSelected) {
      if (!selected.some(t => t.testOutputMasterId === this.selectedThickeningTimeId)) {
        this.selectedThickeningTimeId = matchSelected.testOutputMasterId;
      }
      return;
    }

    selected[0].isMatchSelected = false;
    this.selectedThickeningTimeId = null;
  }

  onThickeningMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onFluidLossMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onFreeFluidMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onRheologyMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onUcaMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onCrushCubeMatchToggle(t: any): void {
    if (!t?.isMatchSelected) {
      t.matchedScId = null;
    }
  }

  onGelStrengthMatchToggle(t: any): void {
    if (!t?.isMatchSelected) { t.matchedScId = null; }
  }

  onFoamStabilityMatchToggle(t: any): void {
    if (!t?.isMatchSelected) { t.matchedScId = null; }
  }

  onFoamCrushMatchToggle(t: any): void {
    if (!t?.isMatchSelected) { t.matchedScId = null; }
  }

  getSelectedStaticGelStrengths(): any[] {
    return this.testData.staticGelStrengths.filter((t: any) => t.isSelected);
  }

  getOtherGelStrengths(current: any): any[] {
    return this.scGelStrengths;
  }

  getSelectedFoamStabilities(): any[] {
    return this.testData.foamStabilities.filter((t: any) => t.isSelected);
  }

  getOtherFoamStabilities(current: any): any[] {
    return this.scFoamStabilities;
  }

  getSelectedFoamCrushes(): any[] {
    return this.testData.foamCrushes.filter((t: any) => t.isSelected);
  }

  getOtherFoamCrushes(current: any): any[] {
    return this.scFoamCrushes;
  }

  selectAllTests(): void {
    this.setAllTestSelection(true);
  }

  selectNoneTests(): void {
    this.setAllTestSelection(false);
  }

  private setAllTestSelection(selected: boolean): void {
    Object.values(this.testData).forEach((arr: any[]) => arr.forEach(t => t.isSelected = selected));
  }

  hasAnyTests(): boolean {
    return Object.values(this.testData).some((arr: any[]) => arr.length > 0);
  }

  getSelectedSlurryName(): string {
    const s = this.slurries.find(sl => sl.slurryID == this.selectedSlurryId);
    return s ? s.ufSlurryID : '';
  }

  getMixName(test: any): string {
    return this.getSelectedSlurryName();
  }

  hasSelectedSlurry(): boolean {
    return !!this.selectedSlurryId;
  }

  getSelectedTestCount(): number {
    return Object.values(this.testData).reduce((sum: number, arr: any[]) => sum + arr.filter(t => t.isSelected).length, 0);
  }

  getSelectedThickeningTimes(): any[] {
    return this.testData.thickeningTimes.filter(t => t.isSelected);
  }

  getOtherSelectedThickeningTimes(current: any): any[] {
    // Radio options = ServiceCompanyThickeningTime rows for this project
    return this.scThickeningTimes.length > 0 ? this.scThickeningTimes : [current];
  }

  getSelectedFreeFluids(): any[] {
    return this.testData.freeFluids.filter(t => t.isSelected);
  }

  getOtherFreeFluids(current: any): any[] {
    return this.scFreeFluids.length > 0 ? this.scFreeFluids : [current];
  }

  getSelectedFluidLosses(): any[] {
    return this.testData.fluidLosses.filter(t => t.isSelected);
  }

  getOtherSelectedFluidLosses(current: any): any[] {
    return this.scFluidLosses.length > 0 ? this.scFluidLosses : [current];
  }

  getSelectedRheologies(): any[] {
    return this.testData.rheologies.filter(t => t.isSelected);
  }

  getOtherRheologies(current: any): any[] {
    return this.scRheologies.length > 0 ? this.scRheologies : [current];
  }

  getSelectedUcaTests(): any[] {
    return this.testData.compressiveStrengthUca.filter(t => t.isSelected);
  }

  getOtherUcaTests(current: any): any[] {
    return this.scUcaTests.length > 0 ? this.scUcaTests : [current];
  }

  getSelectedCrushCubes(): any[] {
    return this.testData.crushCubes.filter(t => t.isSelected);
  }

  getOtherCrushCubes(current: any): any[] {
    return this.scCrushCubes.length > 0 ? this.scCrushCubes : [current];
  }

  // Canonical fluid mixture row order (matches the legacy app's fixed compatibility grid)
  private readonly compatibilityFluidMixtureOrder: string[] = [
    '100% Mud',
    '95% Mud 5% Spacer',
    '75% Mud 25% Spacer',
    '50% Mud 50% Spacer',
    '25% Mud 75% Spacer',
    '5% Mud 95% Spacer',
    '100% Spacer',
    '100% Cement',
    '95% Cement 5% Spacer',
    '95% Cement 5% Mud',
    '75% Cement 25% Spacer',
    '75% Cement 25% Mud',
    '50% Cement 50% Spacer',
    '50% Cement 50% Mud',
    '25% Cement 75% Spacer',
    '25% Cement 75% Mud',
    '5% Cement 95% Spacer',
    '5% Cement 95% Mud'
  ];

  getGroupedSpacerCompatibilities(): { rep: any; readings: any[] }[] {
    const groups = new Map<number, any[]>();
    for (const t of this.testData.spacerCompatibilities) {
      const key = t.testOutputMasterId ?? 0;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }
    return Array.from(groups.values()).map(items => {
      const byMixture = new Map<string, any>();
      for (const item of items) {
        if (item.fluidMixture) byMixture.set(item.fluidMixture, item);
      }
      // Always render every standard fluid mixture row, blank when no reading exists
      const readings = this.compatibilityFluidMixtureOrder.map(
        label => byMixture.get(label) ?? { fluidMixture: label }
      );
      return { rep: items[0], readings };
    });
  }

  toggleSpacerCompatGroup(rep: any, newVal: boolean): void {
    this.testData.spacerCompatibilities
      .filter(t => t.testOutputMasterId === rep.testOutputMasterId)
      .forEach(t => t.isSelected = newVal);
  }

  getSelectedSpacerCompatibilities(): any[] {
    const seen = new Set<number>();
    return this.testData.spacerCompatibilities.filter(t => {
      if (!t.isSelected) return false;
      const key = t.testOutputMasterId ?? 0;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  getOtherSpacerCompatibilities(current: any): any[] {
    return this.scSpacerCompatibilities.length > 0 ? this.scSpacerCompatibilities : [current];
  }

  // Fills in every standard fluid mixture row, blank when no reading exists for it
  private buildFullCompatibilityRows(existing: any[] | null | undefined): any[] {
    const byMixture = new Map<string, any>();
    for (const item of existing ?? []) {
      if (item.fluidMixture) byMixture.set(item.fluidMixture, item);
    }
    return this.compatibilityFluidMixtureOrder.map(
      label => byMixture.get(label) ?? { fluidMixture: label }
    );
  }

  getFullSpacerCompatibilityRowsForMaster(testOutputMasterId: number): any[] {
    const items = this.testData.spacerCompatibilities.filter(t => t.testOutputMasterId === testOutputMasterId);
    return this.buildFullCompatibilityRows(items);
  }

  getFullScSpacerCompatibilityRows(other: any): any[] {
    return this.buildFullCompatibilityRows(other?.readings);
  }

  // Expands each selected compatibility group to all 18 canonical rows (blank where no reading) for report submission
  private buildSubmissionSpacerCompatibilities(): any[] {
    const selectedKeys = new Set(this.getSelectedSpacerCompatibilities().map(t => t.testOutputMasterId ?? 0));
    const result: any[] = [];
    for (const key of selectedKeys) {
      const items = this.testData.spacerCompatibilities.filter(t => (t.testOutputMasterId ?? 0) === key);
      const rep = items[0];
      const byMixture = new Map<string, any>();
      for (const item of items) {
        if (item.fluidMixture) byMixture.set(item.fluidMixture, item);
      }
      for (const label of this.compatibilityFluidMixtureOrder) {
        const existing = byMixture.get(label);
        result.push(existing ?? {
          ...rep,
          fluidMixture: label,
          rpm600: null, rpm300: null, rpm200: null, rpm100: null, rpm60: null,
          rpm30: null, rpm20: null, rpm10: null, rpm6: null, rpm3: null,
          pv: null, yp: null
        });
      }
    }
    return result;
  }

  getSelectedSpacerQaqcTests(): any[] {
    return this.testData.spacerQaqcTests.filter(t => t.isSelected);
  }

  getOtherSpacerQaqcTests(current: any): any[] {
    return this.scSpacerQaqcTests.length > 0 ? this.scSpacerQaqcTests : [current];
  }

  getSpacerQaqcReadingName(labTest: any, titleId: number | null | undefined): string {
    const id = Number(titleId);
    const title = labTest?.readings?.find((r: any) => Number(r?.titleId) === id)?.titleName;
    return title || `Title ${id}`;
  }

  getSelectedSpacerSsstTests(): any[] {
    return this.testData.spacerSsstTests.filter(t => t.isSelected);
  }

  getMaxSpacerSsstReadingsCount(): number {
    return Math.max(10, ...this.testData.spacerSsstTests.map(t => t.readings?.length ?? 0));
  }

  // Pads with blank entries so the SSST table always shows at least 10 reading columns
  getPaddedSsstReadings(readings: any[] | null | undefined): any[] {
    const count = this.getMaxSpacerSsstReadingsCount();
    const source = readings ?? [];
    return Array.from({ length: count }, (_, i) => source[i] ?? {});
  }

  // Used to build a <colgroup> so the reading columns render equal width
  getSsstReadingIndexes(): number[] {
    return Array.from({ length: this.getMaxSpacerSsstReadingsCount() });
  }

  getOtherSpacerSsstTests(current: any): any[] {
    return this.scSpacerSsstTests.length > 0 ? this.scSpacerSsstTests : [current];
  }

  getSelectedSpacerHphtRheologies(): any[] {
    return this.testData.spacerHphtRheologies.filter(t => t.isSelected);
  }

  onSpacerCompatMatchToggle(t: any): void {
    if (!t?.isMatchSelected) t.matchedScId = null;
  }

  onSpacerQaqcMatchToggle(t: any): void {
    if (!t?.isMatchSelected) t.matchedScId = null;
  }

  onSpacerSsstMatchToggle(t: any): void {
    if (!t?.isMatchSelected) t.matchedScId = null;
  }

  formatMinutes(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    const totalMinutes = Math.round(value);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  finish(): void {
    const payload = {
      slurryId: this.selectedSlurryId,
      showUnapprovedTests: this.showUnapprovedTests,
      userEmail: this.userEmail,
      testData: {
        thickeningTimes:          this.testData.thickeningTimes,
        freeFluids:               this.testData.freeFluids,
        fluidLosses:              this.testData.fluidLosses,
        rheologies:               this.testData.rheologies,
        compressiveStrengthUca:   this.testData.compressiveStrengthUca,
        crushCubes:               this.testData.crushCubes,
        spacerCompatibilities:    this.buildSubmissionSpacerCompatibilities(),
        spacerQaqcTests:          this.testData.spacerQaqcTests,
        spacerSsstTests:          this.testData.spacerSsstTests,
        spacerHphtRheologies:     this.testData.spacerHphtRheologies,
        scThickeningTimes:        this.scThickeningTimes,
        scFreeFluids:             this.scFreeFluids,
        scFluidLosses:            this.scFluidLosses,
        scRheologies:             this.scRheologies,
        scUcaTests:               this.scUcaTests,
        scCrushCubes:             this.scCrushCubes,
        scSpacerCompatibilities:  this.scSpacerCompatibilities,
        scSpacerQaqcTests:        this.scSpacerQaqcTests,
        scSpacerSsstTests:        this.scSpacerSsstTests,
        staticGelStrengths:       this.testData.staticGelStrengths,
        foamStabilities:          this.testData.foamStabilities,
        foamCrushes:              this.testData.foamCrushes,
        scGelStrengths:           this.scGelStrengths,
        scFoamStabilities:        this.scFoamStabilities,
        scFoamCrushes:            this.scFoamCrushes
      }
    };

    this.isGenerating = true;
    this.finalReportService.generateFinalReport(this.projectId, payload).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FinalReport_${this.projectId}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.isGenerating = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('Error generating final report:', err);
        this.isGenerating = false;
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
